import { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-1 — MapaGastosHeatmap
// Calendar grid SVG-puro 7×6 (max). Cada célula 44x44 com rx=8, label
// do dia centralizado. Cor por intensidade (low/medium/high/none) +
// border emerald + glow no dia atual. Click abre Popover com top 3
// gastos do dia (drill-down via fetchDayDetails do hook).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL", maximumFractionDigits: 0,
});

const INTENSITY_FILL = {
  low:    "rgba(0, 255, 163, 0.30)",
  medium: "rgba(255, 200, 87, 0.30)",
  high:   "rgba(255, 77, 109, 0.30)",
  none:   "rgba(20, 25, 40, 0.40)",
};
const INTENSITY_STROKE = {
  low:    "rgba(0, 255, 163, 0.40)",
  medium: "rgba(255, 200, 87, 0.40)",
  high:   "rgba(255, 77, 109, 0.40)",
  none:   "rgba(255, 255, 255, 0.06)",
};
const INTENSITY_TEXT = {
  low:    "#00FFA3",
  medium: "#FFC857",
  high:   "#FF4D6D",
  none:   "#64748B",
};

const CELL = 44;
const GAP = 8;
const COLS = 7;

const WEEK_LABELS = ["D", "S", "T", "Q", "Q", "S", "S"];

function DayDetailsPopoverContent({ details, loading, date, onSeeAll }) {
  if (loading) {
    return <p className="text-caption text-foreground-muted">Carregando…</p>;
  }
  if (!details || details.transactions_count === 0) {
    return (
      <div className="text-center py-2">
        <p className="text-caption text-foreground-muted">Sem gastos neste dia</p>
        <p className="text-micro text-foreground-subtle mt-1">
          {format(new Date(date + "T00:00:00"), "EEEE, dd 'de' MMMM", { locale: ptBR })}
        </p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-micro text-foreground-subtle uppercase mb-2" style={{ letterSpacing: "0.08em" }}>
        {format(new Date(date + "T00:00:00"), "EEEE, dd 'de' MMMM", { locale: ptBR })}
      </p>
      <p className="text-h3 font-bold tabular-nums text-foreground mb-3">
        Total: {fmtBRL(details.total)}
      </p>
      <p className="text-caption font-medium text-foreground mb-2">Top 3 gastos</p>
      <ul className="space-y-1.5 mb-3">
        {(details.top_3_transactions || []).map((t, i) => (
          <li key={t.id || i} className="flex items-center gap-2 text-caption">
            <span className="w-1.5 h-1.5 rounded-full bg-status-error shrink-0" />
            <span className="flex-1 truncate text-foreground">{t.description}</span>
            <span className="font-mono tabular-nums text-status-error">{fmtBRL(t.amount)}</span>
          </li>
        ))}
      </ul>
      <Button variant="secondary" size="sm" onClick={onSeeAll} className="w-full">
        Ver todas as {details.transactions_count} transações
      </Button>
    </div>
  );
}

export default function MapaGastosHeatmap({ heatmap, loading, onFetchDayDetails, onSeeAllDay }) {
  const days = heatmap?.days || [];
  const monthStr = heatmap?.month || format(new Date(), "yyyy-MM");
  const totalSpent = heatmap?.total_spent || 0;
  const dailyAverage = heatmap?.daily_average || 0;
  const monthLabel = useMemo(() => {
    try {
      const [yy, mm] = monthStr.split("-").map(Number);
      const d = new Date(yy, mm - 1, 1);
      return format(d, "MMMM 'de' yyyy", { locale: ptBR }).replace(/^./, c => c.toUpperCase());
    } catch { return monthStr; }
  }, [monthStr]);

  const today = new Date();
  const isCurrentMonth = monthStr === format(today, "yyyy-MM");
  const todayDay = isCurrentMonth ? today.getDate() : null;

  // Calcula primeiro dia da semana do mês pra alinhar grid
  const firstDayOfWeek = useMemo(() => {
    if (days.length === 0) return 0;
    try {
      const [yy, mm] = monthStr.split("-").map(Number);
      return new Date(yy, mm - 1, 1).getDay();  // 0=Domingo
    } catch { return 0; }
  }, [days.length, monthStr]);

  const cellsCount = firstDayOfWeek + days.length;
  const rowsCount = Math.ceil(cellsCount / COLS);
  const svgWidth = COLS * CELL + (COLS - 1) * GAP;
  const headerHeight = 22;
  const svgHeight = headerHeight + rowsCount * CELL + (rowsCount - 1) * GAP;

  const [selectedDay, setSelectedDay] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const handleClick = useCallback(async (day) => {
    setSelectedDay(day);
    setDetails(null);
    setDetailsLoading(true);
    const date = `${monthStr}-${String(day).padStart(2, "0")}`;
    const result = await onFetchDayDetails?.(date);
    setDetails(result);
    setDetailsLoading(false);
  }, [monthStr, onFetchDayDetails]);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 flex flex-col" data-testid="mapa-heatmap-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <span className="text-caption font-semibold uppercase text-brand-cyan/80" style={{ letterSpacing: "0.12em" }}>
            Mapa de gastos
          </span>
          <p className="text-caption text-foreground-muted mt-0.5">{monthLabel}</p>
        </div>
        <div className="flex items-center gap-3 text-micro text-foreground-subtle flex-wrap" aria-label="Legenda">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: INTENSITY_TEXT.low }} />
            Baixo
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: INTENSITY_TEXT.medium }} />
            Médio
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: INTENSITY_TEXT.high }} />
            Alto
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 h-[280px] rounded-lg bg-surface-card-hover/40 animate-pulse" />
      ) : (
        <div className="flex-1 flex justify-center" data-testid="mapa-grid">
          <svg
            width={svgWidth}
            height={svgHeight}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            role="img"
            aria-label={`Mapa de gastos de ${monthLabel}`}
          >
            {/* Header dos dias da semana */}
            {WEEK_LABELS.map((wd, i) => (
              <text
                key={`wd-${i}`}
                x={i * (CELL + GAP) + CELL / 2}
                y={14}
                fontSize={10}
                fill="#64748B"
                textAnchor="middle"
                aria-hidden="true"
              >
                {wd}
              </text>
            ))}

            {/* Células dos dias */}
            {days.map((d, idx) => {
              const cellIdx = firstDayOfWeek + idx;
              const col = cellIdx % COLS;
              const row = Math.floor(cellIdx / COLS);
              const x = col * (CELL + GAP);
              const y = headerHeight + row * (CELL + GAP);
              const intensity = d.intensity || "none";
              const isToday = todayDay === d.day;
              const isSelected = selectedDay === d.day;
              const isClickable = d.total > 0 || isToday;

              const dayCellId = `mapa-day-${d.day}`;
              const cellNode = (
                <g
                  className={isClickable ? "cursor-pointer" : ""}
                  onClick={isClickable ? () => handleClick(d.day) : undefined}
                  data-testid={dayCellId}
                >
                  <rect
                    x={x} y={y} width={CELL} height={CELL} rx={8}
                    fill={INTENSITY_FILL[intensity]}
                    stroke={isToday ? "rgba(0, 255, 163, 0.9)" : isSelected ? "rgba(0, 212, 255, 0.7)" : INTENSITY_STROKE[intensity]}
                    strokeWidth={isToday || isSelected ? 1.6 : 1}
                    style={{
                      transition: "fill 200ms, stroke 200ms",
                      filter: isToday ? "drop-shadow(0 0 6px rgba(0,255,163,0.45))" : undefined,
                    }}
                  />
                  <text
                    x={x + CELL / 2} y={y + CELL / 2 + 1}
                    fontSize={12} fontWeight={isToday ? 700 : 500}
                    fill={INTENSITY_TEXT[intensity]}
                    textAnchor="middle" dominantBaseline="middle"
                    style={{ pointerEvents: "none" }}
                  >
                    {d.day}
                  </text>
                  {/* Tooltip nativo via title */}
                  <title>
                    {d.total > 0
                      ? `${fmtBRL(d.total)} · ${d.transactions_count} transaç${d.transactions_count === 1 ? "ão" : "ões"}`
                      : "Sem gastos"}
                  </title>
                </g>
              );

              if (!isClickable) return <g key={dayCellId}>{cellNode}</g>;

              return (
                <Popover
                  key={dayCellId}
                  open={isSelected}
                  onOpenChange={(open) => { if (!open) setSelectedDay(null); }}
                >
                  <PopoverTrigger asChild>
                    {cellNode}
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="center" className="w-72 glass-card-strong border-brand-cyan/30">
                    <DayDetailsPopoverContent
                      details={details}
                      loading={detailsLoading}
                      date={`${monthStr}-${String(d.day).padStart(2, "0")}`}
                      onSeeAll={() => {
                        onSeeAllDay?.(`${monthStr}-${String(d.day).padStart(2, "0")}`);
                        setSelectedDay(null);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              );
            })}
          </svg>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-subtle flex items-center justify-between text-caption text-foreground-muted flex-wrap gap-2">
        <span className="tabular-nums">
          Total no mês: <span className="font-semibold text-foreground">{fmtBRL(totalSpent)}</span>
        </span>
        <span className="tabular-nums">
          Média diária: <span className="font-semibold text-foreground">{fmtBRL(dailyAverage)}</span>
        </span>
      </div>
    </Card>
  );
}
